import { useEffect } from "react";
import { inferFontOverrideSource } from "../../../helpers/fontOverrides";
import { getAccountFontFaces } from "./utils";
import type { AccountFont } from "./utils";

// ponytail: faces stay registered for the session — a handful per account, and
// dropping them would blank previews still on screen. Revisit if accounts start
// shipping hundreds of variants.
const loadedFontFaces = new Map<
  string,
  { url: string; weight: string; style: string; fontFace: FontFace }
>();

/**
 * Loads the account's font files into the document so the font picker can
 * preview each variant in its own typeface, like the scene and Google fonts do.
 */
export function useAccountFontFaces(accountFonts: AccountFont[] | undefined) {
  useEffect(() => {
    if (!accountFonts?.length || typeof FontFace === "undefined") {
      return;
    }

    getAccountFontFaces(accountFonts).forEach((face) => {
      // A stylesheet url is not a valid FontFace src; those load through the
      // player's own font handling instead.
      if (inferFontOverrideSource(face.url) === "stylesheet") {
        return;
      }

      const previous = loadedFontFaces.get(face.fontName);
      if (
        previous?.url === face.url &&
        previous.weight === face.weight &&
        previous.style === face.style
      )
        return;
      if (previous) document.fonts.delete(previous.fontFace);

      const fontFace = new FontFace(face.fontName, `url("${face.url}")`, {
        weight: face.weight,
        style: face.style,
      });
      loadedFontFaces.set(face.fontName, {
        url: face.url,
        weight: face.weight,
        style: face.style,
        fontFace,
      });

      fontFace
        .load()
        .then((loaded) => {
          if (loadedFontFaces.get(face.fontName)?.fontFace === fontFace) {
            document.fonts.add(loaded);
          }
        })
        .catch(() => {
          if (loadedFontFaces.get(face.fontName)?.fontFace === fontFace) {
            loadedFontFaces.delete(face.fontName);
          }
        });
    });
  }, [accountFonts]);
}
